import { createFileRoute, Outlet, useLocation, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { AdminShell } from "@/components/layout/admin-shell";
import { Button } from "@/components/ui/button";
import { splitName } from "@/lib/split-name";
import { appBottomNavItems, appSidebarSections, type AppSidebarActiveKey } from "@/lib/app-sidebar";
import type { RoleKind } from "@/components/vroom/role-pill";

interface WorkRouteUser {
  id: string;
  name: string;
  email: string;
  erpEmployeeNumber?: string;
  role?: RoleKind;
}

export const Route = createFileRoute("/_authed/work")({
  component: WorkLayout,
});

function activeKeyFromPath(pathname: string): AppSidebarActiveKey {
  if (pathname.startsWith("/work/inbox")) return "inbox";
  if (pathname.startsWith("/work/changes")) return "changes";
  return "overzicht";
}

function WorkLayout() {
  const { user } = Route.useRouteContext() as { user: WorkRouteUser };
  const router = useRouter();
  const location = useLocation();
  const { t } = useTranslation();

  const { firstName, lastName } = splitName(user.name);
  const role: RoleKind = user.role ?? "worker";
  const active = activeKeyFromPath(location.pathname);

  function goToNewEntry() {
    void router.navigate({
      to: "/work/new/type",
      search: { date: new Date().toISOString().slice(0, 10) },
    });
  }

  return (
    <AdminShell
      user={{ firstName, lastName, role }}
      section="worker"
      sidebarSections={appSidebarSections({ active, role }, t)}
      bottomNavItems={appBottomNavItems({ section: "worker", active, role }, t)}
      topbarAction={
        <Button variant="primary" size="compact" onClick={goToNewEntry}>
          <Plus size={14} />
          {t("work.fab.add")}
        </Button>
      }
      fab={{ onClick: goToNewEntry, label: t("work.fab.add") }}
    >
      <Outlet />
    </AdminShell>
  );
}
